import { cn } from '~shared/utils/cn'
import Image from 'next/image'
import graduate from './images/graduate.jpg'
import peopleLaughing from './images/people-laughing.jpg'
import { getProgramData } from '~shared/api/single-program-data/single-program-data'


// ─── Types ───────────────────────────────────────────────────────────────────

type TForWhomProps = {
  program: Awaited<ReturnType<typeof getProgramData>>
}

// ─── Default data ─────────────────────────────────────────────────────────────

const DEFAULT_FOR_WHOM = [
  {
    name: 'Выпускники 11 класса',
    description: 'Есть возможность поступить без ЕГЭ по специальной программе',
    image: { url: null, width: null, height: null, alternativeText: null }
  },
  {
    name: 'Для тех, кто хочет перевестись из другого ВУЗа',
    description: 'Перевод из других вузов осуществляется без потери курса',
    image: { url: null, width: null, height: null, alternativeText: null }
  },
  {
    name: 'У кого уже есть образование',
    description: 'По окончании колледжа вы можете поступить на сокращённую форму обучения продолжительностью 3.5 года без сдачи ЕГЭ',
    image: { url: null, width: null, height: null, alternativeText: null }
  },
  {
    name: 'Для тех, у кого есть высшее образование',
    description: 'При наличии диплома о высшем образовании есть возможность получить второе высшее образование по ускоренной программе продолжительностью 3.5 года и без сдачи ЕГЭ',
    image: { url: null, width: null, height: null, alternativeText: null }
  }
]

// ─── Decorations ──────────────────────────────────────────────────────────────

const Vector = ({ className }: { className?: string }) => (
  <div className={cn('flex flex-col absolute', className)}>
    {[0, 1].map(i => (
      <svg key={i} width='89' height='87' viewBox='0 0 89 87' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M81.5062 87C77.2247 87 73.1218 85.284 70.1152 82.2358L2.43233 13.6179C-2.55413 8.56255 1.02706 0 8.12787 0H71.4824C75.7639 0 79.8668 1.71599 82.8735 4.76417L150.556 73.3821C155.543 78.4374 151.962 87 144.861 87H81.5062Z'
          fill='#F1AE2B'
        />
      </svg>
    ))}
  </div>
)

const Rounds = () => (
  <>
    <div className='absolute w-[234px] h-[234px] rounded-full bg-[#F07A56]' style={{ top: '-49%', right: '-18%' }} />
    <div className='absolute w-[234px] h-[234px] rounded-full bg-[#3A52EE]' style={{ top: '49%', right: '-18%' }} />
  </>
)

const CARD_STYLES: Record<number, string> = {
  0: 'bg-[#3A52EE] text-white',
  1: 'bg-[#F2F2F2] text-black',
  2: 'border border-[#C4C4C4] text-black',
  3: 'bg-[#3A52EE] text-white'
}

// ─── Component ────────────────────────────────────────────────────────────────

export const ForWhomSection = ({ program }: TForWhomProps) => {
  const items = program?.forWhom && program.forWhom.length > 0
    ? program.forWhom
    : DEFAULT_FOR_WHOM

  return (
    <section
      className='py-[49px] sm:py-[56px] 2lg:py-[80px]'>
      <div className='mx-auto px-[16px] box-border max-w-[672px] md:max-w-[864px] 2lg:max-w-[1280px]'>
        <h2 className='text-[32px] md:text-[40px] 2lg:text-[48px] font-medium mb-[32px] 2lg:mb-[48px]'>
          Для кого эта программа?
        </h2>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-y-[40px] md:gap-y-[32px] 2lg:gap-y-[56px] gap-x-[32px]'>
          {items.map((item, idx) => {
            const hasImage = Boolean(item.image?.url)
            const showDefaultImage = idx === 0 || idx === 3

            return (
              <div
                key={`ForWhom__${item.name || 'item'}--${idx + 1}`}
                className={cn(
                  'relative flex flex-col gap-[16px] overflow-hidden w-full',
                  'min-h-[214px] md:min-h-[230px] 2lg:min-h-[239px]',
                  'p-[24px] md:p-[32px]',
                  'rounded-[16px]',
                  CARD_STYLES[idx],
                  idx === 0 || idx === 3 ? 'max-w-[592px]' : 'max-w-[488px]',
                  idx < 2 ? '2lg:justify-self-start' : '2lg:justify-self-end'
                )}>

                {(hasImage || showDefaultImage) && (
                  <div className='absolute inset-0 w-full h-full'>
                    <Image
                      src={item.image?.url || (idx === 3 ? graduate : peopleLaughing)}
                      alt={item.image?.alternativeText || (idx === 3 ? 'graduate' : 'people-laughing')}
                      fill
                      quality={100}
                      className='object-cover rounded-[16px]'
                    />
                  </div>
                )}

                {idx === 1 && <Vector className='right-0 2lg:right-0 top-[32px]' />}
                {idx === 2 && <Rounds />}

                <p className='relative text-[20px] font-medium leading-[120%]'>{item.name}</p>
                <p className='relative text-[14px] md:text-[16px] leading-[140%]'>{item.description}</p>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}